<template>
  <!-- The picked contract on its third party's page (US 9369 + 9371): its own Edit and ⋯, apart from the hero's,
       which are always the third party's -->
  <header class="pp-chead" qid="contract-head">
    <span class="pp-chead__icon"><font-awesome-icon icon="file-alt"/></span>

    <div class="pp-chead__main">
      <h2 qid="tp-view-page-title">{{ contract.name }}</h2>
      <div class="pp-chead__meta">
        <span class="pp-scope is-kid">
          <font-awesome-icon icon="file-alt"/>{{ count > 1 ? $t('vendor_merged.contract_of', { index: index, count: count }) : $t('vendor_merged.contract') }}
        </span>
        <span v-if="contract.ended_at" class="sp-badge is-ended" qid="contract-head-ended"><font-awesome-icon icon="archive"/>{{ $t('contract_end.ended_on', { date: endedOn(contract.ended_at) }) }}</span>
        <span v-if="rolesLabel">{{ rolesLabel }}</span>
        <span v-if="contract.type">{{ contract.type | truncate(40) }}</span>
        <span v-if="contract.reference">{{ contract.reference | truncate(30) }}</span>
        <span :class="{ 'is-warn': !owner }">{{ owner ? $t('vendor_merged.owner', { name: owner.name }) : $t('system_hero.no_owner') }}</span>
        <span v-if="!contract.partition && getClient.parent"><font-awesome-icon icon="lock"/> {{ $t('systems_overview.from_workspace', { name: getClient.parent.name }) }}</span>
      </div>
    </div>

    <div class="pp-chead__done" :title="missingText" qid="contract-head-completeness">
      <span class="pp-mini"><i v-for="check in checks" :key="check.key" :class="{ 'is-no': !check.ok }"></i></span>
      <small>{{ $t('system_hero.filled', { done: done, total: checks.length }) }}</small>
    </div>

    <!-- History opens from the header instead of the tab bar, as on projects -->
    <button type="button"
            class="pp-btn"
            :class="{ on: historyOn }"
            v-b-tooltip.hover
            :title="$t('maintenance.third_parties.tab.history')"
            qid="contract-open-history"
            @click="$emit('history')">
      <font-awesome-icon icon="history"/><span class="sr-only">{{ $t('maintenance.third_parties.tab.history') }}</span>
    </button>
    <button v-if="canEdit"
            type="button"
            class="pp-btn"
            qid="edit-tp-button"
            @click="$emit('edit')">
      <font-awesome-icon icon="pen"/>{{ $t('system_hero.edit') }}
    </button>
    <b-dropdown right
                no-caret
                variant="link"
                toggle-class="pp-btn"
                menu-class="sx-menu"
                qid="contract-more">
      <template #button-content>
        <font-awesome-icon icon="ellipsis-h"/><span class="sr-only">{{ $t('system_hero.more') }}</span>
      </template>
      <b-dropdown-item v-if="canEdit" @click="$emit('edit')"><font-awesome-icon icon="pen"/>{{ $t('system_hero.menu.edit') }}</b-dropdown-item>
      <b-dropdown-item v-if="canEdit" qid="contract-move" @click="$emit('move')"><font-awesome-icon icon="exchange-alt"/>{{ $t('contract_hero.menu.move') }}</b-dropdown-item>
      <b-dropdown-item qid="contract-print" @click="print"><font-awesome-icon icon="file-alt"/>{{ $t('system_hero.menu.export') }}</b-dropdown-item>
      <b-dropdown-item v-if="canEdit && !contract.ended_at" qid="contract-end" @click="$emit('end')"><font-awesome-icon icon="archive"/>{{ $t('contract_hero.menu.end') }}</b-dropdown-item>
      <b-dropdown-item v-if="canEdit && contract.ended_at" qid="contract-reopen" @click="$emit('reopen')"><font-awesome-icon icon="undo"/>{{ $t('contract_hero.menu.reopen') }}</b-dropdown-item>
      <template v-if="canDelete">
        <b-dropdown-divider></b-dropdown-divider>
        <b-dropdown-item link-class="is-danger" qid="delete-tp-button" @click="$emit('delete')"><font-awesome-icon icon="trash-alt"/>{{ $t('system_hero.menu.delete') }}</b-dropdown-item>
      </template>
    </b-dropdown>
  </header>
</template>

<script>
  import {endedOn} from '@/util/contractEnd.js';
  import {contractChecks} from '@/util/contractChecks.js';

  export default {
    name: 'ContractHead',
    props: {
      contract: { type: Object, required: true },
      // Its place among the third party's contracts
      index: { type: Number, default: 1 },
      count: { type: Number, default: 1 },
      // The contract's history is open
      historyOn: { type: Boolean, default: false }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      editable() {
        return !this.getClient.parent || this.contract.partition;
      },
      canEdit() {
        return this.isGranted([this.permissions.THIRD_PARTY_EDIT, this.permissions.THIRD_PARTY_EDIT_OWN], this.contract) && this.editable;
      },
      canDelete() {
        return this.isGranted([this.permissions.THIRD_PARTY_DELETE, this.permissions.THIRD_PARTY_DELETE_OWN], this.contract) && this.editable;
      },
      rolesLabel() {
        let roles = this.contract.processing_roles || [];
        return roles.map(role => this.$t('maintenance.third_parties.processing_role.' + this.$options.filters.slugify(role))).join(', ');
      },
      owner() {
        return (this.contract.users || []).find(user => user.owner) || null;
      },
      checks() {
        return contractChecks(this.contract, this.getClient);
      },
      done() {
        return this.checks.filter(check => check.ok).length;
      },
      missingText() {
        let missing = this.checks.filter(check => !check.ok);
        if (!missing.length) {
          return this.$t('system_hero.all_filled');
        }
        let names = missing.map(check => this.$t('contract_hero.missing.' + check.key).toLowerCase());
        return this.$t('system_hero.missing', { list: names.join(', ') });
      }
    },
    methods: {
      endedOn: endedOn,
      print() {
        window.print();
      }
    }
  }
</script>
